// Cobalt below 21 ships a broken TimeRanges implementation: both
// `HTMLMediaElement.buffered` and `SourceBuffer.buffered` intermittently report
// an EMPTY range while data is actually buffered. The Cobalt source comments
// that it is wrong; it is fixed in Cobalt 21. The HiSilicon and Broadcom boxes
// are on 19, so it is still live for most of the fleet.
//
// Shaka 2.5.23 reads both, and an empty read switches off its own recovery:
//
//   - Stall recovery never fires. StallDetector's shouldBeMakingProgress()
//     returns false when `video.buffered.length` is 0, so the moment a stall
//     needs nudging is exactly the moment Shaka decides not to nudge.
//
//   - The buffering goal is never met. StreamingEngine gates on
//     bufferedAheadOf(sourceBuffer), so a stuck-at-empty read means it keeps
//     prefetching instead of idling at `bufferingGoal`. Eviction reads the same
//     value, decides nothing is buffered and skips, so the SourceBuffer grows
//     until QUOTA_EXCEEDED — which Shaka answers by walking its buffering goal
//     down through eight reductions and, when those are exhausted, raising a
//     CRITICAL error that this app turns into the fatal "playback failed" modal.
//
//   - The spinner never clears. pollBufferState_ derives its buffer lead from
//     `video.buffered`, so BufferingObserver stays pinned at STARVING.
//
// Fix: mask the spurious empty reads. Strategy is "sticky last good" — every
// read that returns a non-empty range is remembered; a read that returns empty
// AFTER we have already seen real data (and the element is otherwise healthy)
// returns the remembered range instead of the bogus empty one. The first real
// fill is always passed through untouched, so we never fabricate buffer that
// never existed and a genuine underflow still reaches Shaka.
//
// The memory has to follow what is actually removed, or it becomes the stall it
// exists to prevent. Shaka empties buffers two ways, and they need opposite
// answers:
//
//   - A full clear (`MediaSourceEngine.clear()`, on every audio-track switch and
//     unbuffered seek) removes everything. If the memory survived it,
//     StreamingEngine would read "already at the buffering goal", fetch nothing,
//     and wait forever on data it never requested — a permanent freeze.
//
//   - Eviction (`evict_`, roughly once per appended segment) removes only the
//     front of the range, the oldest data behind the playhead. Forgetting the
//     whole range there would leave the mask disarmed for most of playback,
//     which is the starvation above.
//
// So a removal trims the remembered range and only forgets it when it takes the
// whole thing. `abort()` is deliberately NOT treated as a removal: it resets
// parser state without discarding data, and Shaka calls it on every bitrate
// switch.
//
// This is the buffered shim alone. The pause guard and the stall watchdog that
// used to sit alongside it were written for Shaka 1.6.5 and went with the Cobalt
// 11 boxes (CP-2353): 2.5.23 recovers by itself, provided it can read
// `buffered`. Keeping this half is what lets it.
import { getCobaltMajor, getStvVersionNumber } from "./LoadShaka";
var COBALT_BUFFERED_FIX_VERSION = 21;
var sourceBufferShimInstalled = false;
// Objects already carrying a mask, so re-running the player effect against the
// same <video> node doesn't stack wrappers or listeners.
var shimmedTargets = new WeakSet();
// The masks on <video> elements. `HTMLMediaElement.buffered` is the INTERSECTION
// of the SourceBuffers' ranges, so clearing any one of them genuinely empties
// it — and nothing tells the element that: an MSE wipe fires neither `emptied`
// nor `loadstart`. Every removal is therefore forwarded here as well. One entry
// per <video> the app ever creates, each holding four numbers.
var videoStickies = new Set();
var isCobalt = function () {
    return (navigator.userAgent || "").indexOf("Cobalt") !== -1;
};
// The major from window.STVVersion (e.g. "22.1.6"), or -1 where the box does not
// report one. Builds from 2026-08-06 onward carry it and it is authoritative;
// the user-agent parse is the fallback for everything older.
//
// Reads through getStvVersionNumber so this and the loading screen cannot drift
// on what the field holds. An anchored parse here returned -1 on any box that
// prefixes the field with a chipset name, retiring the source this comment
// calls authoritative and quietly leaving only the fallback.
var stvMajor = function () {
    var version = getStvVersionNumber();
    if (!version) {
        return -1;
    }
    return parseInt(version, 10);
};
// Cobalt only, and only the majors that still carry the bug.
//
// A Cobalt that names no version in either source still gets the shim. Guessing
// "old" is the safe direction: on a healthy TimeRanges the mask never engages,
// because the empty reads it would replace do not happen — while missing it on a
// broken one is an unrecoverable stall. AM-BOX is in the fleet and Qarva has
// never mapped it to a Cobalt version, so this case is not hypothetical.
var shouldShim = function () {
    if (typeof navigator === "undefined") {
        return false;
    }
    var fromStv = stvMajor();
    if (fromStv >= 0) {
        return fromStv < COBALT_BUFFERED_FIX_VERSION;
    }
    var fromUserAgent = getCobaltMajor();
    if (fromUserAgent >= 0) {
        return fromUserAgent < COBALT_BUFFERED_FIX_VERSION;
    }
    return isCobalt();
};
// A minimal TimeRanges-compatible object covering the single coalesced range
// Shaka reads. Every reader either takes start(0)/end(length-1) or iterates by
// index, so one range answers both. It is not a real TimeRanges: an `instanceof`
// check or a caller expecting IndexSizeError would not be satisfied. Nothing in
// Shaka 2.5.23 or this app does either.
var makeRange = function (start, end) {
    var range = {
        length: 1,
        start: function (index) {
            if (index !== 0)
                throw new Error("TimeRanges index out of bounds");
            return start;
        },
        end: function (index) {
            if (index !== 0)
                throw new Error("TimeRanges index out of bounds");
            return end;
        },
    };
    return range;
};
var createStickyRange = function () {
    var start = 0;
    var end = 0;
    var armed = false;
    var suspended = false;
    var forget = function () {
        start = 0;
        end = 0;
        armed = false;
    };
    return {
        // Ignored while a removal is in flight: between remove() and its updateend
        // the native range still describes data that is on its way out, and
        // remembering it there would mask the empty that follows.
        remember: function (from, to) {
            if (suspended) {
                return;
            }
            start = from;
            end = to;
            armed = true;
        },
        forget: forget,
        removed: function (from, to) {
            if (!armed) {
                return;
            }
            if (from <= start && to >= end) {
                // Took the whole memory — this is the deliberate clear.
                forget();
                return;
            }
            if (from <= start && to > start) {
                // Ate the front. Eviction is always this one.
                start = to;
                return;
            }
            if (to >= end && from < end) {
                // Ate the back.
                end = from;
                return;
            }
            if (from > start && to < end) {
                // Split it in two. One range cannot describe that without overstating
                // what is buffered, so drop the memory instead.
                forget();
            }
        },
        masked: function () {
            if (!armed) {
                return undefined;
            }
            return makeRange(start, end);
        },
        suspend: function () {
            suspended = true;
        },
        resume: function () {
            suspended = false;
        },
    };
};
var findBufferedGetter = function (target) {
    var current = target;
    while (current) {
        var descriptor = Object.getOwnPropertyDescriptor(current, "buffered");
        if (descriptor && typeof descriptor.get === "function") {
            return descriptor.get;
        }
        current = Object.getPrototypeOf(current);
    }
    return undefined;
};
// Replace the `buffered` accessor on a single object instance (the prototype
// accessor is shadowed by this own-property, which works even when the native
// descriptor is non-configurable on the prototype). `isHealthy` guards masking
// so a genuinely ended/torn-down element still reports its real empty range.
//
// Returns false when the mask could not be applied — not every Cobalt build
// exposes `buffered` as a redefinable accessor, and a silent no-op would look
// shimmed in the logs while behaving un-shimmed in the field.
var installBufferedMask = function (target, sticky, isHealthy) {
    // findBufferedGetter starts at the target, so masking twice would wrap our own
    // getter instead of the native one.
    if (shimmedTargets.has(target)) {
        return true;
    }
    var nativeGetter = findBufferedGetter(target);
    if (!nativeGetter) {
        console.warn("[player] no buffered accessor to shim — leaving it alone");
        return false;
    }
    try {
        Object.defineProperty(target, "buffered", {
            configurable: true,
            get: function () {
                var native = nativeGetter.call(this);
                if (native && native.length > 0) {
                    sticky.remember(native.start(0), native.end(native.length - 1));
                    return native;
                }
                if (isHealthy()) {
                    var remembered = sticky.masked();
                    if (remembered) {
                        return remembered;
                    }
                }
                return native;
            },
        });
        shimmedTargets.add(target);
        return true;
    }
    catch (error) {
        console.warn("[player] failed to shim buffered getter", error);
        return false;
    }
};
// Teach one SourceBuffer's memory about the data being taken away from it.
// Returns false if the wrapper could not be installed, which has to mean "do not
// mask this object at all": a mask nothing can disarm is a permanent freeze,
// while no mask is only the platform bug we started with.
var followRemovals = function (sourceBuffer, sticky) {
    var nativeRemove = sourceBuffer.remove;
    if (typeof nativeRemove !== "function") {
        return false;
    }
    try {
        Object.defineProperty(sourceBuffer, "remove", {
            configurable: true,
            writable: true,
            value: function (start, end) {
                // Order matters at both ends. Suspend before the native call, because a
                // platform that dispatched updateend synchronously from inside remove()
                // would resume before we suspended and retire the memory for good — and
                // this file exists because Cobalt's MSE is wrong in this very area.
                // Trim after it, so a removal that threw leaves the memory describing
                // what is still really there.
                sticky.suspend();
                try {
                    nativeRemove.call(this, start, end);
                }
                catch (error) {
                    sticky.resume();
                    throw error;
                }
                sticky.removed(start, end);
                // Trims are idempotent, so forwarding every removal is safe as well as
                // correct. These are deliberately not suspended: their latch would be
                // shared by two SourceBuffers removing independently, and one stream's
                // updateend would resume the other's window. The residue is that a read
                // taken between remove() and updateend can re-arm an element mask with a
                // range that is on its way out — bounded, corrected by the next honest
                // read, and the element mask is not in StreamingEngine's refetch loop.
                videoStickies.forEach(function (videoSticky) {
                    videoSticky.removed(start, end);
                });
            },
        });
    }
    catch (error) {
        console.warn("[player] failed to follow SourceBuffer removals", error);
        return false;
    }
    // updateend settles both appends and removals; either way the native range is
    // trustworthy again. error and abort end an operation without it, and this
    // file exists because Cobalt's MSE does not always keep to spec here — a
    // removal that never settled would strand the memory suspended for the rest of
    // the session.
    sourceBuffer.addEventListener("updateend", sticky.resume);
    sourceBuffer.addEventListener("error", sticky.resume);
    sourceBuffer.addEventListener("abort", sticky.resume);
    return true;
};
// Patch the <video> element. Masking is gated on the element still holding
// current data (readyState >= HAVE_CURRENT_DATA) and not having ended, so a real
// underflow — which drops readyState — is reported honestly to Shaka's
// StallDetector.
export var shimVideoBuffered = function (video) {
    if (!shouldShim())
        return;
    // The effect re-runs on every episode and quality change against the same
    // node; the listeners below must not stack.
    if (shimmedTargets.has(video))
        return;
    var sticky = createStickyRange();
    var masked = installBufferedMask(video, sticky, function () { return video.readyState >= 2 && !video.ended; });
    if (!masked)
        return;
    // Shaka detaches by stripping src and calling load(), and attaches by setting
    // a fresh blob src. Both ends of that mark the death of one title's buffer, so
    // the next title does not inherit it.
    video.addEventListener("emptied", sticky.forget);
    video.addEventListener("loadstart", sticky.forget);
    videoStickies.add(sticky);
};
// Patch every SourceBuffer Shaka creates by wrapping MediaSource.addSourceBuffer
// once. The mask arms on the first non-empty read, so before the first append
// the real (empty) range is passed through and the initial buffering gate still
// waits for genuine data.
export var installCobaltBufferedShim = function () {
    if (sourceBufferShimInstalled)
        return;
    sourceBufferShimInstalled = true;
    if (!shouldShim())
        return;
    if (typeof MediaSource === "undefined")
        return;
    var proto = MediaSource.prototype;
    var originalAddSourceBuffer = proto.addSourceBuffer;
    if (typeof originalAddSourceBuffer !== "function")
        return;
    // This runs at module scope, at app boot, on every screen. An unwritable
    // binding here would throw during module evaluation in the strict-mode bundle
    // and take the whole app down before it renders, so a failure has to stay a
    // failure to shim rather than a failure to start.
    try {
        proto.addSourceBuffer = function (type) {
            var sourceBuffer = originalAddSourceBuffer.call(this, type);
            // Everything past this point is ours, and none of it is worth failing a
            // load over — a throw escaping here would reject Shaka's init while
            // leaving the SourceBuffer attached but unknown to it.
            try {
                var sticky = createStickyRange();
                // Probe before wrapping, then disarm before masking (see
                // followRemovals). On a build that does not expose `buffered` as a
                // redefinable accessor the mask cannot go on at all, and the removal
                // feed would then cost every eviction, all session, on a no-JIT box, to
                // maintain state no getter reads.
                if (findBufferedGetter(sourceBuffer) &&
                    followRemovals(sourceBuffer, sticky)) {
                    installBufferedMask(sourceBuffer, sticky, function () { return true; });
                }
            }
            catch (error) {
                console.warn("[player] SourceBuffer shim skipped", error);
            }
            return sourceBuffer;
        };
        if (proto.addSourceBuffer !== originalAddSourceBuffer) {
            console.warn("[player] Cobalt buffered shim installed");
        }
        else {
            // Assigned without throwing and without taking — a silently read-only
            // binding. Saying nothing here is how a box looks shimmed in the logs
            // while behaving un-shimmed in the field.
            console.warn("[player] Cobalt buffered shim did not take");
        }
    }
    catch (error) {
        console.warn("[player] Cobalt buffered shim NOT installed", error);
    }
};
// The boxes that misreport `buffered` are the same ones that wedge mid-stream,
// so the shim's gate is also the stall watchdog's gate.
export var needsStallWatchdog = function () { return shouldShim(); };



//////////////////
// WEBPACK FOOTER
// ./src/utils/CobaltBuffered.ts
// module id = 191
// module chunks = 1